import { Eye } from "@assets"
import { Link, router } from "@inertiajs/react"
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Trash2,
  X,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import { storyViewersPath, userPath } from "@/routes"

import type { Story } from "./stories"

interface StoryViewerProps {
  stories: Story[]
  currentIndex: number
  initialItemIndex?: number
  onClose: () => void
  onNext: () => void
  onPrev: () => void
  onStoryEnd: (storyId: string, itemId: string) => void
  onStoryDeleted?: (storyId: string, itemId: string) => void
  currentUserId?: string
}

export function StoryViewer({
  stories,
  currentIndex,
  initialItemIndex = 0,
  onClose,
  onNext,
  onPrev,
  onStoryEnd,
  onStoryDeleted,
  currentUserId,
}: StoryViewerProps) {
  const getInitials = useInitials()

  const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex)
  const [progress, setProgress] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef(0)
  const elapsedTimeRef = useRef(0)

  const previousStoryIdRef = useRef<string | null>(null)
  const previousStoryIndexRef = useRef<number | null>(null)
  const startXRef = useRef<number | null>(null)
  const startYRef = useRef<number | null>(null)
  const hasMovedRef = useRef(false)
  const didLongPressRef = useRef(false)
  const pressStartTimeRef = useRef<number | null>(null)
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)
  const [animating, setAnimating] = useState(false)

  const minSwipe = 60
  const closeSwipe = 120
  const longPressDelay = 200
  const moveThreshold = 10

  const currentStory = stories[currentIndex]
  const currentItem = currentStory?.items[currentItemIndex]

  // Format time ago for stories
  const formatTimeAgo = (createdAt: string) => {
    const now = new Date()
    const created = new Date(createdAt)
    const diffInHours = Math.floor(
      (now.getTime() - created.getTime()) / (1000 * 60 * 60),
    )

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(
        (now.getTime() - created.getTime()) / (1000 * 60),
      )
      return `hace ${diffInMinutes} min`
    } else if (diffInHours === 1) {
      return "hace 1 hora"
    } else if (diffInHours < 24) {
      return `hace ${diffInHours} horas`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `hace ${diffInDays} día${diffInDays > 1 ? "s" : ""}`
    }
  }

  // Check if current story belongs to current user
  const isOwnStory = currentStory?.userId === currentUserId

  // Handle menu actions
  const handleDeleteStory = () => {
    if (
      currentItem &&
      currentStory &&
      confirm("¿Estás seguro de que quieres eliminar esta historia?")
    ) {
      router.delete(`/historias/${currentItem.id}`, {
        onSuccess: () => {
          if (onStoryDeleted) {
            onStoryDeleted(currentStory.id, currentItem.id)
          }
          onClose()
        },
        onError: (errors) => {
          console.error("Error deleting story:", errors)
        },
      })
    }
  }

  const handleViewStats = () => {
    if (currentItem) {
      router.visit(storyViewersPath(currentItem.id))
    }
  }

  const handleMenuOpen = (open: boolean) => {
    if (open) {
      setIsPaused(true)
      pauseTimer()
    } else {
      setIsPaused(false)
      if (imageLoaded) {
        startTimer()
      }
    }
  }
  const duration = currentItem?.duration ?? 5000

  const lastDeltaXRef = useRef(0)
  const lastDeltaYRef = useRef(0)

  /* ---------------- Timer ---------------- */

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) return
    elapsedTimeRef.current += Date.now() - startTimeRef.current
    clearTimer()
  }, [clearTimer])

  const startTimer = useCallback(() => {
    if (!imageLoaded || isPaused) return

    clearTimer()
    startTimeRef.current = Date.now()

    timerRef.current = setInterval(() => {
      const elapsed =
        elapsedTimeRef.current + (Date.now() - startTimeRef.current)

      const pct = (elapsed / duration) * 100

      if (pct >= 100) {
        clearTimer()
        elapsedTimeRef.current = 0
        setProgress(100)

        onStoryEnd(currentStory.id, currentItem.id)

        if (currentItemIndex < currentStory.items.length - 1) {
          setCurrentItemIndex((i) => i + 1)
          setProgress(0)
          setImageLoaded(false)
        } else {
          onNext()
        }
      } else {
        setProgress(pct)
      }
    }, 50)
  }, [
    clearTimer,
    imageLoaded,
    isPaused,
    duration,
    currentItem,
    currentItemIndex,
    currentStory,
    onNext,
    onStoryEnd,
  ])

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    if (!currentStory) return

    const sameStory =
      previousStoryIdRef.current === currentStory.id &&
      previousStoryIndexRef.current === currentIndex

    if (sameStory) {
      startTimer()
      return
    }

    previousStoryIdRef.current = currentStory.id
    previousStoryIndexRef.current = currentIndex

    const firstUnseen = currentStory.items.findIndex((i) => !i.seen) ?? 0

    setCurrentItemIndex(firstUnseen >= 0 ? firstUnseen : 0)
    setProgress(0)
    setImageLoaded(false)
    elapsedTimeRef.current = 0
    clearTimer()
  }, [currentIndex, currentStory, clearTimer, startTimer])

  useEffect(() => {
    if (isPaused) pauseTimer()
    else if (imageLoaded) startTimer()
    return clearTimer
  }, [isPaused, imageLoaded, startTimer, pauseTimer, clearTimer])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && currentStory && currentItem) {
        onStoryEnd(currentStory.id, currentItem.id)
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [currentStory, currentItem, onStoryEnd, onClose])

  /* ---------------- Pointer ---------------- */

  const getPoint = (e: React.TouchEvent | React.MouseEvent) =>
    "touches" in e
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY }

  const handlePointerStart = (e: React.TouchEvent | React.MouseEvent) => {
    const { x, y } = getPoint(e)
    startXRef.current = x
    startYRef.current = y
    hasMovedRef.current = false
    didLongPressRef.current = false
    pressStartTimeRef.current = Date.now()

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }

    pressTimerRef.current = setTimeout(() => {
      didLongPressRef.current = true
      pauseTimer()
      setIsPaused(true)
    }, longPressDelay)
  }

  const handlePointerMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (startXRef.current == null || startYRef.current == null) return
    const { x, y } = getPoint(e)

    const dx = x - startXRef.current
    const dy = y - startYRef.current

    if (Math.abs(dx) > moveThreshold || Math.abs(dy) > moveThreshold) {
      hasMovedRef.current = true
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current)
        pressTimerRef.current = null
      }
    }

    if (!hasMovedRef.current) return

    lastDeltaXRef.current = dx
    lastDeltaYRef.current = dy

    if (Math.abs(dx) > Math.abs(dy)) {
      setOffsetX(dx)
      setOffsetY(0)
    } else if (dy > 0) {
      setOffsetY(dy)
      setOffsetX(0)
    }
  }

  const resetPointer = () => {
    startXRef.current = null
    startYRef.current = null
    hasMovedRef.current = false
    pressStartTimeRef.current = null
    didLongPressRef.current = false
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }
    lastDeltaXRef.current = 0
    lastDeltaYRef.current = 0
    setOffsetX(0)
    setOffsetY(0)
    setIsPaused(false)
  }

  const handlePointerEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }

    const heldDuration =
      pressStartTimeRef.current != null
        ? Date.now() - pressStartTimeRef.current
        : 0

    const wasLongPress =
      didLongPressRef.current || heldDuration >= longPressDelay
    pressStartTimeRef.current = null

    if (wasLongPress) {
      resetPointer()
      return
    }

    const dx = lastDeltaXRef.current
    const dy = lastDeltaYRef.current

    if (hasMovedRef.current) {
      if (dy > closeSwipe) {
        onStoryEnd(currentStory.id, currentItem.id)
        onClose()
        return
      }

      if (Math.abs(dx) > minSwipe) {
        onStoryEnd(currentStory.id, currentItem.id)
        if (dx > 0) {
          onPrev()
        } else {
          onNext()
        }
        resetPointer()
        return
      }
    }

    setAnimating(true)
    setOffsetX(0)
    setOffsetY(0)
    setTimeout(() => setAnimating(false), 250)

    resetPointer()
  }

  /* ---------------- Tap ---------------- */

  const handleTap = (side: "left" | "right") => {
    if (didLongPressRef.current || hasMovedRef.current) return
    onStoryEnd(currentStory.id, currentItem.id)
    if (side === "left") {
      handlePrev()
    } else {
      handleNext()
    }
  }

  const handleNext = () => {
    if (currentItemIndex < currentStory.items.length - 1)
      setCurrentItemIndex((i) => i + 1)
    else onNext()
  }

  const handlePrev = () => {
    if (currentItemIndex > 0) setCurrentItemIndex((i) => i - 1)
    else onPrev()
  }

  if (!currentStory || !currentItem) return null

  return (
    <div
      className="fixed inset-0 top-[calc(env(safe-area-inset-top,0px))] z-[9999] flex items-center justify-center bg-black/90"
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        touchAction: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className={cn(
          "relative h-full w-full cursor-pointer overflow-hidden bg-black transition-transform md:h-[720px] md:w-[420px] md:rounded-xl",
          animating && "duration-300",
        )}
        style={{ transform: `translate(${offsetX}px, ${offsetY}px)` }}
        onMouseDown={handlePointerStart}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerEnd}
        onTouchStart={handlePointerStart}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerEnd}
      >
        {/* Progress */}
        <div className="absolute top-0 right-0 left-0 z-30 flex gap-1 p-2">
          {currentStory.items.map((item, i) => (
            <div key={item.id} className="h-0.5 flex-1 rounded bg-white/30">
              <div
                className="h-full bg-white"
                style={{
                  width:
                    i < currentItemIndex
                      ? "100%"
                      : i === currentItemIndex
                        ? `${progress}%`
                        : "0%",
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 right-0 left-0 z-30 flex justify-between px-3">
          <Link
            href={userPath(currentStory.userId)}
            className="flex items-center gap-2"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={currentStory.userAvatar ?? undefined}
                alt={currentStory.userName ?? ""}
              />
              <AvatarFallback className="text-primary gradient-avatar p-1 font-semibold">
                {getInitials(currentStory.userName ?? "")}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-white">
                {currentStory.userName}
              </span>
              <span className="text-xs text-white/70">
                {currentItem && formatTimeAgo(currentItem.createdAt)}
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            {/* Menu for own stories */}
            {isOwnStory && (
              <DropdownMenu onOpenChange={handleMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="bottom"
                  className="bg-background border-border z-[9999] min-w-[200px]"
                  forceMount
                >
                  <DropdownMenuItem
                    onClick={handleViewStats}
                    className="text-primary hover:bg-muted cursor-pointer"
                  >
                    <Eye className="fill-primary mr-2 h-4 w-4" />
                    Ver visualizaciones
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDeleteStory}
                    className="text-destructive hover:bg-muted cursor-pointer"
                  >
                    <Trash2 className="text-destructive mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button
              onClick={() => {
                onStoryEnd(currentStory.id, currentItem.id)
                onClose()
              }}
              className="cursor-pointer"
            >
              <X className="text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        {currentItem.url ? (
          <img
            src={currentItem.url}
            className="h-full w-full object-cover select-none"
            onLoad={() => setImageLoaded(true)}
            draggable={false}
          />
        ) : (
          <div className="bg-muted flex h-full w-full items-center justify-center text-white">
            <span>Sin contenido</span>
          </div>
        )}

        {/* Caption overlay */}
        {currentItem.caption && (
          <div className="absolute right-0 bottom-6 left-0 z-20 px-4">
            <div className="flex items-start gap-2">
              <Avatar className="mt-0.5 h-6 w-6 flex-shrink-0">
                <AvatarImage
                  src={currentStory.userAvatar ?? undefined}
                  alt={currentStory.userName ?? ""}
                />
                <AvatarFallback className="text-primary gradient-avatar text-[10px] font-semibold">
                  {getInitials(currentStory.userName ?? "")}
                </AvatarFallback>
              </Avatar>
              <p className="text-sm leading-snug font-medium text-white drop-shadow-lg">
                {currentItem.caption}
              </p>
            </div>
          </div>
        )}

        {/* Tap zones */}
        <div className="absolute inset-0 z-20 flex">
          <div
            className="w-1/3 cursor-pointer"
            onClick={() => handleTap("left")}
          />
          <div className="w-1/3" />
          <div
            className="w-1/3 cursor-pointer"
            onClick={() => handleTap("right")}
          />
        </div>

        {/* Arrows web */}
        <button
          onClick={handlePrev}
          className="absolute top-1/2 left-2 z-40 hidden -translate-y-1/2 cursor-pointer text-white md:flex"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={handleNext}
          className="absolute top-1/2 right-2 z-40 hidden -translate-y-1/2 cursor-pointer text-white md:flex"
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  )
}
